import { router } from "@inertiajs/react"
import { Plus, Scale, Rocket, AlertTriangle } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import type { SkillVersionRow } from "@/types/skill"
import type { RehearsalSummary } from "@/types/test_lab"

type Props = {
  skillId: number
  selectedVersion: number
  versions: SkillVersionRow[]
  isGlobal: boolean
  onSwitchVersion: (versionId: number) => void
  // Promote-gate state. When undefined (e.g. on pages outside the Test Lab
  // that don't compute the gate), the button stays enabled and the server
  // does the safety check. When supplied, the UI greys out the button +
  // captions it so the operator knows why.
  canPromote?: boolean
  blockerCount?: number
  // Used to render the "Promote anyway" confirm bullets when the gate is
  // blocked. Optional so callers outside Test Lab can omit them.
  rehearsal?: RehearsalSummary | null
}

// Top-bar version selector: dropdown + Promote / Create / Compare actions.
// Compare is reserved for v1.1 (page exists in plan, not built in 2A).
export function VersionSelector({
  skillId,
  selectedVersion,
  versions,
  isGlobal,
  onSwitchVersion,
  canPromote,
  blockerCount,
  rehearsal,
}: Props) {
  const selected = versions.find((v) => v.version === selectedVersion) ?? versions[0]
  const isDraft = selected?.status === "draft"
  const isLive  = selected?.status === "live"
  const writable = !isGlobal && isDraft
  // Gate-blocked iff the caller passed a flag AND it's false. When the
  // flag is undefined (consumer didn't compute it) we don't pre-empt.
  const gateBlocked = canPromote === false
  const promoteDisabled = !writable || gateBlocked
  const promoteTitle = gateBlocked
    ? `Tests must pass before going live (${blockerCount ?? 0} test${blockerCount === 1 ? "" : "s"} failing)`
    : undefined

  function createDraft() {
    if (!confirm(`Clone the latest version into a new draft?`)) return
    router.post(`/skills/${skillId}/draft_version`, {}, { preserveScroll: false })
  }

  function promote() {
    if (!selected) return
    if (!confirm(`Promote v${selected.version} to live? Production traffic switches over immediately.`)) return
    // Route :id is the parent Skill id (stable across versions). The
    // selected SkillVersion is identified server-side via the skill's
    // current draft.
    router.patch(`/skills/${skillId}/promote`, {}, { preserveScroll: false })
  }

  // Bypass the promote gate. Server logs the bypass and decorates the
  // success flash with a warning prefix.
  function promoteAnyway() {
    if (!selected) return
    const bullets: string[] = []
    if (typeof blockerCount === "number" && blockerCount > 0) {
      bullets.push(`${blockerCount} test${blockerCount === 1 ? "" : "s"} on this skill haven't passed yet`)
    }
    const blocker = typeof rehearsal?.blocker === "string" ? rehearsal.blocker : null
    if (blocker === "no_rehearsal_yet") {
      bullets.push("Pre-launch test hasn't run yet")
    } else if (blocker === "rehearsal_running") {
      bullets.push("Pre-launch test is running")
    } else if (blocker === "rehearsal_failed") {
      bullets.push(`Pre-launch test failed (${rehearsal?.failedCount ?? 0} regressions)`)
    } else if (blocker === "rehearsal_errored") {
      bullets.push("Pre-launch test crashed")
    }
    const list = bullets.map((b) => ` \u2022 ${b}`).join("\n")
    const msg =
      `Promote anyway? This bypasses test gates. Bypassing:\n${list}\n\nType / continue to confirm.`
    if (!confirm(msg)) return
    router.patch(`/skills/${skillId}/promote`, { force: true }, { preserveScroll: false })
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-app-muted">Version</span>
        <select
          value={selectedVersion}
          onChange={(e) => onSwitchVersion(Number(e.target.value))}
          className="h-9 rounded-control border border-border bg-white/5 px-3 text-sm text-app-text outline-none transition focus:border-brand-red/50"
        >
          {versions.map((v) => (
            <option key={v.id} value={v.version}>
              v{v.version} · {v.status}
            </option>
          ))}
        </select>
        {selected && (
          <Badge tone={isLive ? "gold" : isDraft ? "red" : "neutral"}>
            {selected.status}
          </Badge>
        )}
      </div>

      <span className="h-5 w-px bg-border" aria-hidden />

      <Button variant="ghost" size="sm" onClick={createDraft} disabled={isGlobal}>
        <Plus size={14} strokeWidth={2} className="mr-1" />
        New version
      </Button>

      <Button variant="ghost" size="sm" disabled title="Coming in v1.1">
        <Scale size={14} strokeWidth={2} className="mr-1" />
        Compare
      </Button>

      {/* Gate state is exclusive — show one button. Green: standard
          Promote. Blocked + writable: red outline "Promote anyway" with
          confirm. Blocked + read-only: disabled standard button. */}
      <div className="flex flex-col items-end gap-1">
        {gateBlocked && writable ? (
          <button
            type="button"
            onClick={promoteAnyway}
            className="inline-flex items-center gap-1 rounded-control border border-status-error/40 bg-status-error/10 px-3 py-1.5 text-xs text-status-error transition hover:bg-status-error/20"
            title="Bypass promote gate — logs an audit warning"
          >
            <AlertTriangle size={12} strokeWidth={2.2} />
            Promote anyway
          </button>
        ) : (
          <Button variant="red" size="sm" onClick={promote} disabled={promoteDisabled} title={promoteTitle}>
            <Rocket size={14} strokeWidth={2} className="mr-1" />
            Promote draft
          </Button>
        )}
        {gateBlocked && (
          <span className="inline-flex items-center gap-1 text-[10px] text-status-warning">
            <AlertTriangle size={10} strokeWidth={2.2} />
            Tests must pass before going live{typeof blockerCount === "number" && blockerCount > 0 ? ` (${blockerCount})` : ""}
          </span>
        )}
      </div>
    </div>
  )
}
